import React from 'react';
import { useState } from 'react';
import { 
  Calendar, Download, Code, Cpu, Database, Feather, 
  TrendingUp, TrendingDown, Clock, Target, Award, 
  BookOpen, Eye, Zap, Brain, BarChart3, PieChart,
  Activity, Users, Star, ArrowUp, ArrowDown,
  Play, Pause, RotateCcw, Filter, ChevronRight
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Pie, Tooltip, Legend, ResponsiveContainer, PieChart as RechartsPieChart, Cell, BarChart, Bar, RadialBarChart, RadialBar, AreaChart, Area } from 'recharts';

const LearningAnalytics = ({darkMode, setDarkMode }) => {
  
  const [activeMetric, setActiveMetric] = useState('overview');
  const [timeRange, setTimeRange] = useState('30d');

  // Sample data for analytics
  const learningTrendData = [
    { date: 'Jan 1', reading: 45, courses: 23, news: 12, discovery: 8, total: 88 },
    { date: 'Jan 8', reading: 52, courses: 31, news: 18, discovery: 14, total: 115 },
    { date: 'Jan 15', reading: 38, courses: 28, news: 22, discovery: 19, total: 107 },
    { date: 'Jan 22', reading: 65, courses: 42, news: 15, discovery: 23, total: 145 },
    { date: 'Jan 29', reading: 48, courses: 35, news: 28, discovery: 17, total: 128 },
    { date: 'Feb 5', reading: 71, courses: 38, news: 31, discovery: 25, total: 165 },
    { date: 'Feb 12', reading: 59, courses: 45, news: 19, discovery: 21, total: 144 }
  ];

  const learningDistribution = [
    { name: 'Reading Books', value: 45, color: '#3B82F6', sessions: 124 },
    { name: 'Online Courses', value: 32, color: '#8B5CF6', sessions: 89 },
    { name: 'News & Articles', value: 15, color: '#10B981', sessions: 156 },
    { name: 'Discovery', value: 8, color: '#F59E0B', sessions: 67 }
  ];

  const performanceMetrics = [
    { category: 'Focus Score', current: 87, previous: 82, trend: 'up', description: 'Time spent without distractions' },
    { category: 'Retention Rate', current: 74, previous: 71, trend: 'up', description: 'Knowledge retained after 7 days' },
    { category: 'Completion Rate', current: 92, previous: 88, trend: 'up', description: 'Content finished vs started' },
    { category: 'Consistency', current: 68, previous: 75, trend: 'down', description: 'Daily learning streak maintenance' }
  ];

  const skillProgression = [
    { skill: 'JavaScript', level: 85, growth: '+12%', color: '#F7DF1E' },
    { skill: 'Machine Learning', level: 72, growth: '+8%', color: '#FF6B6B' },
    { skill: 'Database Design', level: 68, growth: '+15%', color: '#4ECDC4' },
    { skill: 'UI/UX Design', level: 59, growth: '+6%', color: '#45B7D1' },
    { skill: 'Data Analysis', level: 76, growth: '+9%', color: '#96CEB4' }
  ];

  const weeklyHeatmap = [
    { day: 'Mon', hours: [2, 3, 1, 4, 2, 3, 1] },
    { day: 'Tue', hours: [1, 2, 4, 2, 3, 2, 1] },
    { day: 'Wed', hours: [3, 1, 2, 3, 1, 4, 2] },
    { day: 'Thu', hours: [2, 4, 1, 2, 3, 1, 3] },
    { day: 'Fri', hours: [1, 2, 3, 1, 2, 3, 2] },
    { day: 'Sat', hours: [4, 3, 2, 3, 4, 2, 1] },
    { day: 'Sun', hours: [2, 1, 3, 2, 1, 2, 3] }
  ];

  const getHeatmapColor = (intensity) => {
    const colors = ['#1f2937', '#374151', '#4b5563', '#6b7280', '#9ca3af'];
    return colors[Math.min(intensity, 4)];
  };

  const insights = [
    {
      type: 'positive',
      title: 'Excellent Progress in Programming',
      description: 'Your JavaScript skills have improved by 12% this month, placing you in the top 15% of learners.',
      action: 'Continue with advanced topics',
      icon: Code
    },
    {
      type: 'warning',
      title: 'Consistency Needs Attention',
      description: 'Your learning streak dropped to 4 days. Try setting smaller daily goals to maintain momentum.',
      action: 'Set 15-min daily reminders',
      icon: Target
    },
    {
      type: 'info',
      title: 'Reading Speed Optimization',
      description: 'You\'re reading 23% faster than last month but comprehension remained stable at 87%.',
      action: 'Try speed reading techniques',
      icon: BookOpen
    }
  ];

  const recommendations = [
    {
      title: 'Advanced React Patterns',
      reason: 'Based on your JavaScript progress',
      confidence: 94,
      estimatedTime: '6 hours',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=200&fit=crop'
    },
    {
      title: 'Deep Learning Fundamentals',
      reason: 'Complementary to your ML studies',
      confidence: 87,
      estimatedTime: '12 hours',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=200&fit=crop'
    },
    {
      title: 'PostgreSQL Advanced Queries',
      reason: 'Next step in database mastery',
      confidence: 91,
      estimatedTime: '4 hours',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=200&fit=crop'
    }
  ];

  return (
    <div className={`min-h-screen transition-all duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-light mb-2">Learning Analytics</h1>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Advanced insights into your learning journey
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>           
            
            
            <button className={`px-4 py-2 rounded-xl flex items-center space-x-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}>
              <Download className="w-4 h-4" />
              <span>Export Report</span>
            </button>
          </div>
        </div>

        {/* Key Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {performanceMetrics.map((metric, index) => (
            <div key={index} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg hover:shadow-xl transition-all duration-300`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {metric.category}
                </h3>
                <div className={`flex items-center space-x-1 ${metric.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                  {metric.trend === 'up' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                  <span className="text-xs font-medium">
                    {Math.abs(metric.current - metric.previous)}%
                  </span>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="text-3xl font-bold mb-1">{metric.current}%</div>
                <div className={`w-full bg-gray-200 rounded-full h-2 ${darkMode ? 'bg-gray-700' : ''}`}>
                  <div 
                    className={`h-2 rounded-full ${metric.trend === 'up' ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{ width: `${metric.current}%` }}
                  ></div>
                </div>
              </div>
              
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {metric.description}
              </p>
            </div>
          ))}
        </div>

        {/* Learning Trend Chart */}
        <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg mb-8`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-light">Learning Activity Trends</h2>
            <div className="flex items-center space-x-2">
              <button className={`px-3 py-1 text-sm rounded-full ${activeMetric === 'overview' ? (darkMode ? 'bg-blue-600' : 'bg-blue-500') + ' text-white' : (darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300')}`}
                onClick={() => setActiveMetric('overview')}>
                Overview
              </button>
              <button className={`px-3 py-1 text-sm rounded-full ${activeMetric === 'detailed' ? (darkMode ? 'bg-blue-600' : 'bg-blue-500') + ' text-white' : (darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300')}`}
                onClick={() => setActiveMetric('detailed')}>
                Detailed
              </button>
            </div>
          </div>
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {activeMetric === 'overview' ? (
                <AreaChart data={learningTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                  <XAxis dataKey="date" stroke={darkMode ? '#9CA3AF' : '#6B7280'} />
                  <YAxis stroke={darkMode ? '#9CA3AF' : '#6B7280'} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area type="monotone" dataKey="total" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} strokeWidth={3} />
                </AreaChart>
              ) : (
                <LineChart data={learningTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                  <XAxis dataKey="date" stroke={darkMode ? '#9CA3AF' : '#6B7280'} />
                  <YAxis stroke={darkMode ? '#9CA3AF' : '#6B7280'} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="reading" stroke="#3B82F6" strokeWidth={3} dot={{ r: 6 }} />
                  <Line type="monotone" dataKey="courses" stroke="#8B5CF6" strokeWidth={3} dot={{ r: 6 }} />
                  <Line type="monotone" dataKey="news" stroke="#10B981" strokeWidth={3} dot={{ r: 6 }} />
                  <Line type="monotone" dataKey="discovery" stroke="#F59E0B" strokeWidth={3} dot={{ r: 6 }} />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Learning Distribution */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h2 className="text-2xl font-light mb-6">Learning Distribution</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={learningDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {learningDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {learningDistribution.map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <div>
                    <div className="text-sm font-medium">{item.name}</div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {item.sessions} sessions
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Activity Heatmap */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <h2 className="text-2xl font-light mb-6">Weekly Activity Heatmap</h2>
            <div className="space-y-2">
              {weeklyHeatmap.map((day, dayIndex) => (
                <div key={dayIndex} className="flex items-center space-x-2">
                  <div className={`w-12 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {day.day}
                  </div>
                  <div className="flex space-x-1">
                    {day.hours.map((intensity, hourIndex) => (
                      <div
                        key={hourIndex}
                        className="w-4 h-4 rounded-sm"
                        style={{ backgroundColor: getHeatmapColor(intensity) }}
                        title={`${intensity} hours of learning`}
                      ></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4 text-xs text-gray-500">
              <span>Less</span>
              <div className="flex space-x-1">
                {[0, 1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: getHeatmapColor(level) }}
                  ></div>
                ))}
              </div>
              <span>More</span>
            </div>
          </div>
        </div>

        {/* Skill Progression */}
        <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg mb-8`}>
          <h2 className="text-2xl font-light mb-6">Skill Progression</h2>
          <div className="space-y-4">
            {skillProgression.map((skill, index) => (
              <div key={index} className="flex items-center space-x-4">
                <div className="w-24 text-sm font-medium">{skill.skill}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className={`w-full bg-gray-200 rounded-full h-3 ${darkMode ? 'bg-gray-700' : ''}`}>
                      <div 
                        className="h-3 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${skill.level}%`,
                          backgroundColor: skill.color
                        }}
                      ></div>
                    </div>
                    <div className="ml-4 text-sm font-medium">{skill.level}%</div>
                  </div>
                </div>
                <div className="text-sm text-green-500 font-medium w-12">
                  {skill.growth}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Insights */}
        <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg mb-8`}>
          <div className="flex items-center space-x-2 mb-6">
            <Brain className="w-6 h-6 text-purple-500" />
            <h2 className="text-2xl font-light">AI-Powered Insights</h2>
          </div>
          <div className="space-y-4">
            {insights.map((insight, index) => (
              <div key={index} className={`p-4 rounded-xl border-l-4 ${
                insight.type === 'positive' ? 'border-green-500 bg-green-50' :
                insight.type === 'warning' ? 'border-yellow-500 bg-yellow-50' :
                'border-blue-500 bg-blue-50'
              } ${darkMode ? 'bg-opacity-10' : ''}`}>
                <div className="flex items-start space-x-3">
                  <insight.icon className={`w-5 h-5 mt-1 ${
                    insight.type === 'positive' ? 'text-green-500' :
                    insight.type === 'warning' ? 'text-yellow-500' :
                    'text-blue-500'
                  }`} />
                  <div className="flex-1">
                    <h3 className="font-medium mb-1">{insight.title}</h3>
                    <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {insight.description}
                    </p>
                    <button className={`text-sm font-medium flex items-center space-x-1 ${
                      insight.type === 'positive' ? 'text-green-600' :
                      insight.type === 'warning' ? 'text-yellow-600' :
                      'text-blue-600'
                    } hover:opacity-80 transition-opacity`}>
                      <span>{insight.action}</span>
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Personalized Recommendations */}
        <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-light">Personalized Recommendations</h2>
            <button className={`text-sm ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'} transition-colors`}>
              View All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recommendations.map((rec, index) => (
              <div key={index} className={`rounded-xl overflow-hidden ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-all duration-300 cursor-pointer group`}>
                <img 
                  src={rec.image} 
                  alt={rec.title}
                  className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                      {rec.confidence}% match
                    </span>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {rec.estimatedTime}
                    </span>
                  </div>
                  <h3 className="font-medium mb-2">{rec.title}</h3>
                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {rec.reason}
                  </p>
                  <button className={`w-full py-2 text-sm rounded-lg ${darkMode ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors flex items-center justify-center space-x-2`}>
                    <Play className="w-4 h-4" />
                    <span>Start Learning</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningAnalytics;